import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PublicLayout } from '../../components/layout/PublicLayout';
// NOTE: Le composant CampaignCard devra être cloné et renommé/restylé en "ProjectCard"
import { CampaignCard as ProjectCard } from '../../components/crowdfunding';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Pagination } from '../../components/ui/pagination';
import { CrowdfundingCampaign } from '../../types/crowdfunding';
import { campaignService } from '../../services/crowdfunding';
// Icônes adaptées
import { HeartHandshake, Loader2, Palette } from 'lucide-react';
import { useTenantContext } from '../../context/TenantContext';

export const CrowdfundingList: React.FC = () => {
  // Simplification : les filtres correspondent aux catégories de soutien
  // NOTE: Il faudra ajouter un champ "category" ('monumental', 'atelier', etc.) à vos campagnes dans la base de données
  const [activeCategory, setActiveCategory] = useState<'all' | 'monumental' | 'atelier'>('all');
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 6; // 6 projets par page pour un affichage plus aéré
  
  const { tenant } = useTenantContext();
  const tenantId = tenant?.id || '2';

  // Configuration SEO personnalisée par tenant
  const seoConfig = React.useMemo(() => {
    const seo = tenant?.config?.seo || {};
    const crowdfundingHeader = tenant?.config?.pages?.crowdfunding?.header || {};
    const currentDomain = window.location.hostname;
    
    return {
      title: `${crowdfundingHeader.title || 'Soutenir l\'Artiste'} | ${tenant?.name || 'Mécénat'}`,
      description: seo.description || crowdfundingHeader.line1 || `Participez à la création des prochaines œuvres de ${tenant?.name || 'l\'artiste'}. Votre soutien est la pierre angulaire de ce travail.`,
      image: seo.ogImage || tenant?.logo || tenant?.icon,
      url: `https://${currentDomain}/crowdfunding`
    };
  }, [tenant]);
  
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['mecenat-projets', activeCategory, currentPage, pageSize, tenantId],
    queryFn: async () => {
      const category = activeCategory === 'all' ? undefined : activeCategory;
      return await campaignService.getCampaigns({ 
        // NOTE: Adapter le service pour accepter un filtre par catégorie
        category: category as any, 
        page: currentPage,
        pageSize: pageSize,
        tenantId: tenantId,
        status: 'active' // On affiche que les projets actifs
      });
    }
  });
  
  useEffect(() => {
    setCurrentPage(0);
  }, [activeCategory]);

  useEffect(() => {
    refetch();
  }, [currentPage, refetch]);
  
  const projects = data?.campaigns || [];
  const totalCount = data?.totalCount || 0;
  const totalPages = Math.ceil(totalCount / pageSize);
  
  return (
    <PublicLayout>
      <div className="pt-10">
        <SEOMetaTags
          title={seoConfig.title}
          description={seoConfig.description}
          image={seoConfig.image}
          url={seoConfig.url}
        />
        
        <div className="min-h-screen relative">
          {/* Fond d'écran cohérent avec l'identité du site */}
          <div 
            className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
            style={{
              backgroundImage: tenant ? `url(${tenant.config.bg})` : 'url(/default-bg.jpg)',
              backgroundAttachment: 'fixed'
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/90 to-black/95 backdrop-blur-[3px]"></div>
          </div>
          
          <div className="relative z-10 container mx-auto px-4 py-12">
            {/* En-tête de la page */}
            <div className="text-center mb-12">
              <div className="flex justify-center mb-6">
                  <div className="relative">
                      <div className="absolute -inset-4 bg-gradient-to-r from-red-700 via-red-500 to-red-700 rounded-full blur-lg opacity-50"></div>
                      <HeartHandshake className="w-16 h-16 text-red-500 relative" />
                  </div>
              </div>
              <h1 className="text-4xl md:text-5xl font-serif font-bold text-white mb-4">
                Devenez Mécène
              </h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Chaque œuvre est une aventure collective. Votre soutien est la force qui me permet de transformer la matière brute en mémoire vivante.
              </p>
            </div>
            
            {/* Filtres par catégorie de soutien */}
            <div className="flex justify-center items-center gap-4 mb-12">
              <Tabs 
                value={activeCategory} 
                onValueChange={(value) => setActiveCategory(value as any)}
                className="w-full md:w-auto"
              >
                <TabsList className="bg-black/50 border border-white/10 backdrop-blur-sm">
                  <TabsTrigger className="text-gray-200 data-[state=active]:bg-red-600 data-[state=active]:text-white data-[state=active]:font-semibold" value="all">
                    Tous les Projets
                  </TabsTrigger>
                  <TabsTrigger className="text-gray-200 data-[state=active]:bg-red-600 data-[state=active]:text-white data-[state=active]:font-semibold" value="monumental">
                    Projets Monumentaux
                  </TabsTrigger>
                  <TabsTrigger className="text-gray-200 data-[state=active]:bg-red-600 data-[state=active]:text-white data-[state=active]:font-semibold" value="atelier">
                    Soutien à l'Atelier
                  </TabsTrigger>
                </TabsList>
              </Tabs>
            </div>
            
            {isLoading ? (
              <div className="flex justify-center items-center min-h-[400px]">
                <Loader2 className="animate-spin h-12 w-12 text-red-500" />
              </div>
            ) : projects.length > 0 ? (
              <>
                <div className={`grid ${projects.length === 1 ? 'justify-center' : 'justify-items-start'} grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8`} style={{
                  gridTemplateColumns: projects.length === 1 ? 'minmax(auto, 400px)' : undefined
                }}>
                  {projects.map((project) => (
                    // Le composant ProjectCard devra être stylisé avec la palette rouge
                    <ProjectCard key={project.id} campaign={project} />
                  ))}
                </div>
                
                {totalPages > 1 && (
                  <div className="flex justify-center mt-12">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                      // Vous pouvez styliser la pagination avec des couleurs rouges
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl">
                <Palette className="h-16 w-16 text-red-500/50 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-white mb-2">Aucun projet à soutenir pour le moment</h3>
                <p className="text-gray-400 max-w-md mx-auto">
                  De nouvelles opportunités de mécénat seront bientôt disponibles. En attendant, découvrez mes œuvres terminées.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};